import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';

import { BrandThemeConfig } from './brands/types';
import { lightTheme, lightThemeOptions as baseLightThemeOptions } from './theme';

/**
 * Builds MUI theme for Hale (and future non-student brands).
 * Student portal keeps the original lightTheme unchanged.
 */
export const createBrandTheme = (brand: BrandThemeConfig): Theme => {
  const { palette, cssVariables, tokens } = brand;

  return createTheme(baseLightThemeOptions as ThemeOptions, {
    typography: {
      fontFamily: brand.fontFamily,
    },
    palette: {
      background: {
        default: cssVariables['--brand-background'],
        paper:
          cssVariables['--brand-surface'] ||
          cssVariables['--brand-background'],
      },
      tertiary: {
        main: palette.primary,
        dark: palette.green,
        green: palette.green,
        light: `${palette.primary}1F`,
        primary: palette.primary,
        secondary: palette.accent,
      },
      secondary: {
        main: palette.secondary,
        yellow_100: palette.yellow,
        yellow_500: tokens.tabIndicator,
        yellow_600: tokens.toggleActiveBg,
        yellow_700: tokens.toggleActiveBorder,
        quaternary: palette.accent,
      },
      quaternary: {
        main: palette.accent,
        secondary: palette.accent,
      },
      text: {
        primary: cssVariables['--brand-text'],
        quaternary: tokens.tabInactiveText,
        quinary: tokens.tabInactiveText,
      },
    },
    custom: {
      gradients: {
        GREEN_100: `linear-gradient(180deg, ${palette.primary} 0%, ${palette.green} 100%)`,
        GREEN_200: `linear-gradient(180deg, ${palette.accent}33 0%, ${palette.primary} 100%)`,
        highlightedNav: tokens.navActiveGradient,
        YELLOW_100: tokens.buttonPrimaryGradient,
        YELLOW_200: tokens.chipDefaultGradient,
        primaryButton: tokens.buttonPrimaryGradient,
        progress: tokens.progressGradient,
      },
      emptyStateBackground: cssVariables['--brand-empty-state-bg'],
      brandTokens: tokens,
    },
    components: {
      MuiTabs: {
        styleOverrides: {
          indicator: {
            background: tokens.tabIndicator,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: tokens.tabInactiveText,
            textTransform: 'capitalize',
            '&.Mui-selected': {
              color: tokens.tabActiveText,
              fontWeight: 700,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'capitalize',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: tokens.linkColor,
          },
        },
      },
    },
  });
};

/** Student uses original Sparkl theme; other brands use createBrandTheme. */
export const resolveAppTheme = (brand: BrandThemeConfig): Theme =>
  brand.key === 'student' ? lightTheme : createBrandTheme(brand);
